// Visual building blocks for the main marketing page: brand mark, animated backdrops,
// motion helpers and the product mocks shown in the hero, bento and showcase.
import { Fragment, useEffect, useRef, useState } from "react"
import type { ReactNode } from "react"
import { Sparkles, TrendingUp } from "lucide-react"
import { useInView } from "@/components/shared/reveal"

// ─── Brand ───────────────────────────────────────────────────────────────────

export const BRAND_RED = "#C0392B"
export const BRAND_GREEN = "#4A8C3F"
export const BRAND_BLUE = "#2E5BA8"
export const BRAND_COLORS = [BRAND_RED, BRAND_GREEN, BRAND_BLUE]

/** Red square + green circle + blue triangle — the "NOT" in THE NOT FIT. */
export function BrandMark({ size = 10, className = "" }: { size?: number; className?: string }) {
  return (
    <span aria-hidden className={`inline-flex items-center gap-[2px] leading-none flex-shrink-0 ${className}`}>
      <span className="block" style={{ width: size, height: size, backgroundColor: BRAND_RED }} />
      <span className="block rounded-full" style={{ width: size, height: size, backgroundColor: BRAND_GREEN }} />
      <svg width={size} height={size} viewBox="0 0 12 12" className="block">
        <polygon points="0,0 12,0 6,12" fill={BRAND_BLUE} />
      </svg>
    </span>
  )
}

export function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false)
  useEffect(() => {
    const query = window.matchMedia?.("(prefers-reduced-motion: reduce)")
    if (!query) return
    setReduced(query.matches)
    const onChange = () => setReduced(query.matches)
    query.addEventListener?.("change", onChange)
    return () => query.removeEventListener?.("change", onChange)
  }, [])
  return reduced
}

// ─── Backdrops ───────────────────────────────────────────────────────────────

/** Drifting tri-colour aurora with film grain, for the dark sections. */
export function AuroraBackdrop({ className = "" }: { className?: string }) {
  return (
    <div aria-hidden className={`pointer-events-none absolute inset-0 overflow-hidden tnf-grain ${className}`}>
      <div className="tnf-aurora" />
    </div>
  )
}

type FloatingShape = {
  shape: "square" | "circle" | "triangle"
  color: string
  size: number
  top: string
  left: string
  /** Parallax travel in px at the edge of the screen; bigger reads as closer. */
  depth: number
  look: "outline" | "solid" | "soft"
  duration: number
  reverse?: boolean
  desktopOnly?: boolean
}

// The logo's square, circle and triangle drifting at different depths.
const FLOATING_SHAPES: FloatingShape[] = [
  { shape: "square",   color: BRAND_RED,   size: 120, top: "66%", left: "-7%", depth: 14, look: "outline", duration: 30, desktopOnly: true },
  { shape: "triangle", color: BRAND_BLUE,  size: 120, top: "13%", left: "61%", depth: 22, look: "outline", duration: 36, reverse: true, desktopOnly: true },
  { shape: "circle",   color: BRAND_GREEN, size: 260, top: "58%", left: "26%", depth: 8,  look: "soft",    duration: 32 },
  { shape: "square",   color: BRAND_BLUE,  size: 16,  top: "24%", left: "46%", depth: 34, look: "solid",   duration: 18, desktopOnly: true },
  { shape: "circle",   color: BRAND_RED,   size: 12,  top: "93%", left: "4%",  depth: 30, look: "solid",   duration: 16, reverse: true },
  { shape: "triangle", color: BRAND_GREEN, size: 20,  top: "22%", left: "38%", depth: 40, look: "solid",   duration: 20, desktopOnly: true },
  { shape: "circle",   color: BRAND_BLUE,  size: 10,  top: "64%", left: "50%", depth: 46, look: "solid",   duration: 14 },
  { shape: "square",   color: BRAND_GREEN, size: 12,  top: "90%", left: "70%", depth: 26, look: "solid",   duration: 22, reverse: true },
]

function ShapeGlyph({ shape, color, look }: Pick<FloatingShape, "shape" | "color" | "look">) {
  const paint = look === "outline"
    ? { fill: "none", stroke: color, strokeWidth: 2.5, opacity: 0.35 }
    : { fill: color, opacity: look === "soft" ? 0.16 : 0.6 }
  return (
    <svg viewBox="0 0 100 100" className="h-full w-full overflow-visible" style={look === "soft" ? { filter: "blur(28px)" } : undefined}>
      {shape === "square" && <rect x="4" y="4" width="92" height="92" rx="10" {...paint} />}
      {shape === "circle" && <circle cx="50" cy="50" r="46" {...paint} />}
      {shape === "triangle" && <polygon points="6,8 94,8 50,92" strokeLinejoin="round" {...paint} />}
    </svg>
  )
}

// A heartbeat complex every 300 units along a flat baseline.
const PULSE_PATH = [60, 360, 660, 960].reduce(
  (d, x) => `${d} L${x} 120 L${x + 22} 120 L${x + 30} 110 L${x + 38} 120 L${x + 50} 120 L${x + 58} 132 L${x + 68} 36 L${x + 78} 170 L${x + 88} 120 L${x + 108} 120 L${x + 124} 102 L${x + 142} 120`,
  "M-20 120",
) + " L1220 120"

/** A heart-rate line in the brand colours, with a bright pulse travelling along it. */
function PulseLine({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 1200 200" preserveAspectRatio="xMidYMid slice" className={className} fill="none" aria-hidden>
      <defs>
        <linearGradient id="tnf-pulse" x1="0" y1="0" x2="1200" y2="0" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#D9412F" />
          <stop offset="35%" stopColor="#E8743B" />
          <stop offset="65%" stopColor="#4A9A3F" />
          <stop offset="100%" stopColor="#2F6FD0" />
        </linearGradient>
      </defs>
      <path d={PULSE_PATH} stroke="url(#tnf-pulse)" strokeWidth="1.5" strokeLinejoin="round" opacity="0.18" />
      <path d={PULSE_PATH} pathLength={1000} className="tnf-pulse-trace" stroke="url(#tnf-pulse)" strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" opacity="0.18" />
      <path d={PULSE_PATH} pathLength={1000} className="tnf-pulse-trace" stroke="url(#tnf-pulse)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

/**
 * Hero background: a soft breathing wash, the brand shapes floating at different depths (they
 * lean toward the pointer) and a heart-rate line tracing across.
 */
export function HeroBackdrop() {
  const ref = useRef<HTMLDivElement>(null)
  const reduced = usePrefersReducedMotion()

  useEffect(() => {
    const el = ref.current
    if (reduced || !el) return
    let frame = 0
    let x = 0
    let y = 0
    const apply = () => {
      frame = 0
      el.style.setProperty("--px", x.toFixed(3))
      el.style.setProperty("--py", y.toFixed(3))
    }
    const onMove = (e: PointerEvent) => {
      x = (e.clientX / window.innerWidth - 0.5) * 2
      y = (e.clientY / window.innerHeight - 0.5) * 2
      if (!frame) frame = requestAnimationFrame(apply)
    }
    window.addEventListener("pointermove", onMove, { passive: true })
    return () => {
      window.removeEventListener("pointermove", onMove)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [reduced])

  return (
    <div ref={ref} aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      <div
        className="absolute inset-0 tnf-grain tnf-breathe opacity-90 dark:opacity-100"
        style={{
          background: `radial-gradient(55% 50% at 10% 15%, ${BRAND_RED}24, transparent 70%), radial-gradient(50% 45% at 90% 18%, ${BRAND_BLUE}24, transparent 70%), radial-gradient(45% 40% at 45% 100%, ${BRAND_GREEN}1c, transparent 70%)`,
        }}
      />
      {FLOATING_SHAPES.map((s, i) => (
        <div
          key={i}
          className={`absolute transition-transform duration-500 ease-out ${s.desktopOnly ? "hidden xl:block" : ""}`}
          style={{
            top: s.top,
            left: s.left,
            width: s.size,
            height: s.size,
            transform: `translate3d(calc(var(--px, 0) * ${s.depth}px), calc(var(--py, 0) * ${s.depth}px), 0)`,
          }}
        >
          <div className="tnf-tumble h-full w-full" style={{ animationDuration: `${s.duration}s`, animationDirection: s.reverse ? "reverse" : "normal" }}>
            <ShapeGlyph shape={s.shape} color={s.color} look={s.look} />
          </div>
        </div>
      ))}
      <PulseLine className="absolute inset-x-0 -bottom-14 h-[200px] w-full [mask-image:linear-gradient(to_right,transparent,#000_12%,#000_88%,transparent)]" />
    </div>
  )
}

// ─── Text helpers ────────────────────────────────────────────────────────────

/** Renders text with hyphenated words ("all-in-one", "box-et") kept on one line. */
export function KeepHyphenated({ text }: { text: string }) {
  return (
    <>
      {text.split(" ").map((word, i) => (
        <Fragment key={i}>
          {i > 0 && " "}
          {word.includes("-") ? <span className="whitespace-nowrap">{word}</span> : word}
        </Fragment>
      ))}
    </>
  )
}

// ─── Motion helpers ──────────────────────────────────────────────────────────

/**
 * Cycles through words in place. All words share one grid cell, so the widest one sets the
 * size and nothing around it shifts. The visible words are drawn from a data attribute (CSS
 * generated content), so the heading's real text — what screen readers and search engines
 * read — is just `srText`.
 */
export function RotatingWord({ words, srText, className = "", wordClassName = "", interval = 2600 }: { words: string[]; srText: string; className?: string; wordClassName?: string; interval?: number }) {
  const [index, setIndex] = useState(0)
  const reduced = usePrefersReducedMotion()

  useEffect(() => {
    setIndex(0)
    if (reduced || words.length < 2) return
    const timer = setInterval(() => setIndex(i => (i + 1) % words.length), interval)
    return () => clearInterval(timer)
  }, [words, interval, reduced])

  return (
    <>
      <span className="sr-only">{srText}</span>
      <span aria-hidden className={`inline-grid align-top ${className}`}>
        {words.map((word, i) => (
          <span
            key={word}
            data-text={word}
            style={{ gridArea: "1 / 1" }}
            className={`tnf-word transition-all duration-500 ease-out ${word.includes("-") ? "whitespace-nowrap" : ""} ${wordClassName} ${
              i === index ? "opacity-100 translate-y-0 blur-0" : "opacity-0 translate-y-4 blur-sm"
            }`}
          />
        ))}
      </span>
    </>
  )
}

const FIGURE = /^([^\d]*)([\d.,]+)(.*)$/

/** Counts up to a figure like "10,000+" or "99.9%" once scrolled into view, keeping its prefix/suffix. */
export function CountUp({ value, className = "", style }: { value: string; className?: string; style?: React.CSSProperties }) {
  const { ref, inView } = useInView<HTMLSpanElement>()
  const reduced = usePrefersReducedMotion()
  const [display, setDisplay] = useState(() => {
    const match = value.match(FIGURE)
    return match ? `${match[1]}0${match[3]}` : value
  })

  useEffect(() => {
    const match = value.match(FIGURE)
    if (!match || reduced) {
      setDisplay(value)
      return
    }
    if (!inView) return
    const [, prefix, digits, suffix] = match
    const separator = digits.includes(",") ? "," : digits.includes(".") && digits.split(".")[1]?.length === 3 ? "." : ""
    const decimals = !separator && digits.includes(".") ? digits.split(".")[1].length : 0
    const target = parseFloat(separator ? digits.split(separator).join("") : digits)
    const duration = 1400
    const start = performance.now()
    let frame = 0
    const step = (now: number) => {
      const progress = Math.min(1, (now - start) / duration)
      const eased = 1 - Math.pow(1 - progress, 3)
      const current = target * eased
      const formatted = decimals
        ? current.toFixed(decimals)
        : Math.round(current).toLocaleString("en-US").replace(/,/g, separator || "")
      setDisplay(`${prefix}${formatted}${suffix}`)
      if (progress < 1) frame = requestAnimationFrame(step)
    }
    frame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame)
  }, [inView, value, reduced])

  return <span ref={ref} className={className} style={style}>{display}</span>
}

/** Endless horizontal ticker; pauses on hover. */
export function Marquee({ children }: { children: ReactNode }) {
  return (
    <div className="tnf-marquee-mask relative overflow-hidden">
      <div className="tnf-marquee flex w-max items-center">
        <div className="flex items-center gap-3 pr-3">{children}</div>
        <div className="flex items-center gap-3 pr-3" aria-hidden>{children}</div>
      </div>
    </div>
  )
}

export function FloatingChip({ className = "", delay = "0s", children }: { className?: string; delay?: string; children: ReactNode }) {
  return (
    <div
      className={`tnf-glass tnf-float absolute z-20 rounded-2xl p-3 shadow-[0_24px_60px_-24px_rgba(10,10,20,0.45)] ${className}`}
      style={{ animationDelay: delay }}
    >
      {children}
    </div>
  )
}

// ─── Frames ──────────────────────────────────────────────────────────────────

export function BrowserChrome({ children, url = "app.thenotfit.com" }: { children: ReactNode; url?: string }) {
  return (
    <div className="rounded-2xl border bg-background shadow-[0_40px_120px_-40px_rgba(10,10,30,0.45)] overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b bg-secondary/50">
        <div className="flex gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: BRAND_RED, opacity: 0.75 }} />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-400/80" />
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: BRAND_GREEN, opacity: 0.75 }} />
        </div>
        <div className="mx-auto flex min-w-0 max-w-[60%] flex-1 items-center justify-center gap-1.5 rounded-md bg-background px-3 py-1 text-[11px] text-muted-foreground tnf-mono">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="shrink-0" aria-hidden>
            <rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" />
          </svg>
          <span className="truncate">{url}</span>
        </div>
        <div className="w-10" />
      </div>
      {children}
    </div>
  )
}

/** Phone frame around the member-app screen recording. The video only loads once scrolled near. */
export function PhoneMockup({ videoSrc }: { videoSrc: string }) {
  const { ref, inView } = useInView<HTMLDivElement>("0px 0px 200px 0px")
  return (
    <div ref={ref} className="relative" style={{ width: 280 }}>
      <div className="relative rounded-[44px] p-[3px]" style={{ background: "linear-gradient(145deg, #3a3a3c 0%, #1c1c1e 50%, #3a3a3c 100%)", filter: "drop-shadow(0 32px 64px rgba(0,0,0,0.45))" }}>
        <div className="absolute -left-[3px] top-[88px] w-[3px] h-8 rounded-l-full bg-zinc-700" />
        <div className="absolute -left-[3px] top-[132px] w-[3px] h-12 rounded-l-full bg-zinc-700" />
        <div className="absolute -left-[3px] top-[156px] w-[3px] h-12 rounded-l-full bg-zinc-700" />
        <div className="absolute -right-[3px] top-[120px] w-[3px] h-16 rounded-r-full bg-zinc-700" />
        <div className="rounded-[42px] overflow-hidden bg-black">
          <div className="relative bg-black flex justify-center pt-3 pb-1 px-6">
            <div className="w-24 h-6 rounded-full bg-black border border-zinc-800 flex items-center justify-center gap-2">
              <div className="w-2 h-2 rounded-full bg-zinc-700" />
              <div className="w-3 h-3 rounded-full bg-zinc-700" />
            </div>
          </div>
          <div className="relative bg-zinc-900" style={{ aspectRatio: "9/18" }}>
            {inView && <video src={videoSrc} autoPlay muted loop playsInline className="w-full h-full object-cover" />}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
          </div>
          <div className="bg-black flex justify-center py-2">
            <div className="w-24 h-1 rounded-full bg-zinc-600" />
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Bento visuals ───────────────────────────────────────────────────────────

const BRAND_VARIANTS = [
  { name: "Iron House",   accent: "#E63946", bg: "#111111", fg: "#f5f5f5" },
  { name: "Pulse Studio", accent: "#FFD60C", bg: "#18181b", fg: "#f5f5f5" },
  { name: "Coast Club",   accent: "#2E5BA8", bg: "#fafaf9", fg: "#1B1917" },
]

/** One platform, three differently branded gyms. */
export function WhiteLabelVisual() {
  return (
    <div className="grid grid-cols-3 gap-2.5" aria-hidden>
      {BRAND_VARIANTS.map((brand, i) => (
        <div
          key={brand.name}
          className="rounded-xl p-2.5 shadow-lg tnf-float-slow"
          style={{ backgroundColor: brand.bg, color: brand.fg, animationDelay: `${i * 0.6}s` }}
        >
          <div className="flex items-center gap-1.5 mb-2.5">
            <span className="h-4 w-4 rounded-md" style={{ backgroundColor: brand.accent }} />
            <span className="text-[10px] font-bold truncate">{brand.name}</span>
          </div>
          <div className="h-10 rounded-md mb-2 flex items-end p-1.5" style={{ backgroundColor: `${brand.accent}26` }}>
            <span className="text-[8px] font-semibold" style={{ color: brand.accent }}>Book a class</span>
          </div>
          <div className="space-y-1">
            <div className="h-1.5 rounded-full w-full opacity-20" style={{ backgroundColor: brand.fg }} />
            <div className="h-1.5 rounded-full w-2/3 opacity-20" style={{ backgroundColor: brand.fg }} />
          </div>
          <div className="mt-2.5 h-5 rounded-md text-[8px] font-bold flex items-center justify-center" style={{ backgroundColor: brand.accent, color: brand.bg }}>
            Check in
          </div>
        </div>
      ))}
    </div>
  )
}

/** Recent payments with statuses. */
export function PaymentsVisual() {
  const rows = [
    { what: "Monthly membership", amount: "€49",  status: "Paid",        color: BRAND_GREEN },
    { what: "Annual plan",        amount: "€420", status: "Paid",        color: BRAND_GREEN },
    { what: "PT pack ×10",        amount: "€250", status: "Instalment 2/3", color: BRAND_BLUE },
    { what: "Renewal reminder",   amount: "€49",  status: "Sent",        color: BRAND_RED },
  ]
  return (
    <div className="rounded-xl border bg-background/70 p-3 text-xs" aria-hidden>
      <div className="flex items-center justify-between mb-2.5">
        <span className="tnf-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">This month</span>
        <span className="inline-flex items-center gap-1 font-semibold text-emerald-600">
          <TrendingUp className="h-3 w-3" strokeWidth={2.5} /> €18,240
        </span>
      </div>
      <div className="divide-y">
        {rows.map(row => (
          <div key={row.what} className="flex items-center gap-2 py-1.5">
            <span className="flex-1 truncate">{row.what}</span>
            <span className="font-semibold tabular-nums">{row.amount}</span>
            <span className="rounded-full px-1.5 py-0.5 text-[9px] font-semibold" style={{ backgroundColor: `${row.color}1A`, color: row.color }}>
              {row.status}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

/** Weekly attendance with a churn-risk callout. */
export function AnalyticsVisual() {
  const week = [52, 64, 58, 71, 80, 46, 38]
  const max = Math.max(...week)
  const { ref, inView } = useInView<HTMLDivElement>()
  return (
    <div ref={ref} className="rounded-xl border bg-background/70 p-3" aria-hidden>
      <div className="flex items-center justify-between mb-3">
        <span className="tnf-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Attendance · 7 days</span>
        <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ backgroundColor: `${BRAND_RED}1A`, color: BRAND_RED }}>
          4 members at risk
        </span>
      </div>
      <div className="flex items-end gap-2 h-20">
        {week.map((v, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1 h-full justify-end">
            <div
              className={`w-full rounded-t-md ${inView ? "tnf-draw-bar" : "opacity-0"}`}
              style={{ height: `${(v / max) * 100}%`, backgroundColor: i === 4 ? BRAND_BLUE : `${BRAND_BLUE}40`, animationDelay: `${i * 70}ms` }}
            />
            <span className="text-[9px] text-muted-foreground">{"MTWTFSS"[i]}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Showcase mocks ──────────────────────────────────────────────────────────

const TENANT_SHOWCASE_THEMES = [
  { accent: "#E63946", dark: true,  bg: "#111111", text: "#f5f5f5", muted: "#a1a1aa" },
  { accent: "#FFD60C", dark: true,  bg: "#111111", text: "#f5f5f5", muted: "#a1a1aa" },
  { accent: "#1B1917", dark: false, bg: "#fafaf9", text: "#1B1917", muted: "#78716c" },
]

export function DietPlannerMock({ themeIdx = 0 }: { themeIdx?: number }) {
  const th = TENANT_SHOWCASE_THEMES[themeIdx]
  return (
    <div className="p-5 md:p-6" style={{ color: th.text, backgroundColor: th.bg }}>
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.18em] mb-1" style={{ color: th.muted }}>AI-GENERATED PLAN</div>
          <div className="text-lg font-bold">Weight Loss Plan</div>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md" style={{ backgroundColor: `${th.accent}20` }}>
          <Sparkles className="w-3.5 h-3.5" style={{ color: th.accent }} />
          <span className="text-[11px] font-semibold" style={{ color: th.accent }}>AI</span>
        </div>
      </div>
      <div className="flex gap-1 mb-5 overflow-x-auto pb-1">
        {["Day 1", "Day 2", "Day 3", "Day 4", "Day 5", "Day 6", "Day 7"].map((day, i) => (
          <div
            key={day}
            className="px-2.5 py-1 rounded-md text-[11px] font-medium whitespace-nowrap"
            style={i === 0
              ? { backgroundColor: th.accent, color: "#fff" }
              : { backgroundColor: th.dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", color: th.muted }
            }
          >
            {day}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        {[
          { meal: "Breakfast", time: "7:30",  foods: [{ n: "Greek yogurt", c: 180 }, { n: "Whole-grain toast", c: 120 }, { n: "Green tea", c: 5 }],  kcal: 305 },
          { meal: "Lunch",     time: "12:30", foods: [{ n: "Chicken salad", c: 350 }, { n: "Quinoa", c: 185 }, { n: "Olive oil", c: 90 }],            kcal: 625 },
          { meal: "Dinner",    time: "19:00", foods: [{ n: "Baked salmon", c: 380 }, { n: "Steamed broccoli", c: 55 }, { n: "Brown rice", c: 215 }],   kcal: 650 },
        ].map((card, i) => (
          <div key={card.meal} className={`rounded-lg p-3 ${i > 0 ? "hidden sm:block" : ""}`} style={{ backgroundColor: th.dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)" }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold">{card.meal}</span>
              <span className="text-[10px]" style={{ color: th.muted }}>{card.time}</span>
            </div>
            <div className="space-y-1 mb-2">
              {card.foods.map(food => (
                <div key={food.n} className="flex items-center justify-between text-[11px]">
                  <span className="truncate mr-2">{food.n}</span>
                  <span className="font-medium">{food.c}</span>
                </div>
              ))}
            </div>
            <div className="border-t pt-1.5 flex justify-between items-center text-[10px]" style={{ borderColor: th.dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)" }}>
              <span style={{ color: th.muted }}>Total</span>
              <span className="font-bold" style={{ color: th.accent }}>{card.kcal} kcal</span>
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-lg p-3" style={{ backgroundColor: th.dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)" }}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold">Daily macros</span>
          <span className="text-[11px] font-semibold" style={{ color: th.accent }}>1,580 / 1,650 kcal</span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: th.dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)" }}>
          <div className="h-full" style={{ width: "92%", backgroundColor: th.accent }} />
        </div>
      </div>
    </div>
  )
}

export function WorkoutMock({ themeIdx = 1 }: { themeIdx?: number }) {
  const th = TENANT_SHOWCASE_THEMES[themeIdx]
  return (
    <div className="p-5 md:p-6" style={{ color: th.text, backgroundColor: th.bg }}>
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.18em] mb-1" style={{ color: th.muted }}>AI-GENERATED PLAN</div>
          <div className="text-lg font-bold">Full Body Strength</div>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: `${th.accent}25`, color: th.accent }}>Intermediate</span>
            <span className="text-[10px]" style={{ color: th.muted }}>45 min</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md" style={{ backgroundColor: `${th.accent}20` }}>
          <Sparkles className="w-3.5 h-3.5" style={{ color: th.accent }} />
          <span className="text-[11px] font-semibold" style={{ color: th.accent }}>AI</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-5">
        {["Chest", "Back", "Legs", "Core", "Shoulders"].map(muscle => (
          <span key={muscle} className="text-[10px] px-2.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: `${th.accent}15`, color: th.accent }}>
            {muscle}
          </span>
        ))}
      </div>
      <div className="rounded-lg overflow-hidden mb-4" style={{ backgroundColor: th.dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)" }}>
        <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] font-semibold tracking-wider uppercase" style={{ color: th.muted, borderBottom: `1px solid ${th.dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}` }}>
          <div className="col-span-5">Exercise</div>
          <div className="col-span-2 text-center">Sets</div>
          <div className="col-span-2 text-center">Reps</div>
          <div className="col-span-3 text-center">Rest</div>
        </div>
        {[
          { name: "Barbell Bench Press", sets: 4, reps: "8-10", rest: "90s" },
          { name: "Bent-Over Row",       sets: 4, reps: "8-10", rest: "90s" },
          { name: "Barbell Squat",       sets: 4, reps: "6-8",  rest: "120s" },
          { name: "Overhead Press",      sets: 3, reps: "10-12", rest: "60s" },
          { name: "Plank Hold",          sets: 3, reps: "45s",   rest: "45s" },
        ].map((ex, i, arr) => (
          <div key={ex.name} className="grid grid-cols-12 gap-2 px-3 py-2 text-xs items-center" style={{ borderBottom: i < arr.length - 1 ? `1px solid ${th.dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}` : undefined }}>
            <div className="col-span-5 font-medium truncate">{ex.name}</div>
            <div className="col-span-2 text-center font-semibold" style={{ color: th.accent }}>{ex.sets}</div>
            <div className="col-span-2 text-center">{ex.reps}</div>
            <div className="col-span-3 text-center" style={{ color: th.muted }}>{ex.rest}</div>
          </div>
        ))}
      </div>
      <div className="rounded-lg p-3" style={{ backgroundColor: th.dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)" }}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold">Workout progress</span>
          <span className="text-[11px] font-semibold" style={{ color: th.accent }}>3 of 5 completed</span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: th.dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)" }}>
          <div className="h-full" style={{ width: "60%", backgroundColor: th.accent }} />
        </div>
      </div>
    </div>
  )
}

export function PromoMock({ comingSoonLabel }: { comingSoonLabel: string }) {
  const th = TENANT_SHOWCASE_THEMES[2]
  return (
    <div className="relative p-5 md:p-6" style={{ color: th.text, backgroundColor: th.bg }}>
      <div className="filter blur-[2px] opacity-60 pointer-events-none select-none">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-[10px] font-semibold tracking-[0.18em] mb-1" style={{ color: th.muted }}>AI-GENERATED PROMOTION</div>
            <div className="text-lg font-bold">Summer Membership Drive</div>
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md" style={{ backgroundColor: `${th.accent}15` }}>
            <Sparkles className="w-3.5 h-3.5" style={{ color: th.accent }} />
            <span className="text-[11px] font-semibold" style={{ color: th.accent }}>AI</span>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-5">
          {[
            { title: "New Member Welcome",  discount: "30%", audience: "New Sign-ups",     period: "First Month" },
            { title: "Annual Plan Upgrade", discount: "20%", audience: "Monthly Members",  period: "Limited Time" },
            { title: "Referral Bonus",      discount: "€25", audience: "Active Members",   period: "Ongoing" },
          ].map(promo => (
            <div key={promo.title} className="rounded-lg p-3" style={{ backgroundColor: "rgba(0,0,0,0.03)" }}>
              <div className="text-xl font-bold mb-1" style={{ color: th.accent }}>{promo.discount}</div>
              <div className="text-xs font-semibold mb-2">{promo.title}</div>
              <div className="space-y-0.5 text-[10px]" style={{ color: th.muted }}>
                <div className="flex justify-between"><span>Target</span><span>{promo.audience}</span></div>
                <div className="flex justify-between"><span>Period</span><span>{promo.period}</span></div>
              </div>
            </div>
          ))}
        </div>
        <div className="rounded-lg p-3" style={{ backgroundColor: "rgba(0,0,0,0.03)" }}>
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: "Predicted Conversion", value: "24%" },
              { label: "Estimated Revenue",    value: "€3,200" },
              { label: "Members Targeted",     value: "450" },
            ].map(stat => (
              <div key={stat.label} className="text-center">
                <div className="text-base font-bold" style={{ color: th.accent }}>{stat.value}</div>
                <div className="text-[10px]" style={{ color: th.muted }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-foreground text-background font-semibold text-xs tracking-wider shadow-xl">
          <Sparkles className="w-3.5 h-3.5" />
          {comingSoonLabel}
        </div>
      </div>
    </div>
  )
}
